import { deDE, enUS } from '@mui/material/locale';
import { createTheme as createMuiTheme } from '@mui/material/styles';
import { createBreakpoints } from '@mui/system';
import { mergeDeepRight } from 'ramda';
import { Locale, ThemeType } from '@zurich/common/types';
import { components as dsComponents, } from '../../theme';
import * as color from './color';
import * as font from './font';
function createTranslation(locale) {
    switch (locale) {
        case Locale.DE:
            return createTranslationDe();
        case Locale.EN:
            return createTranslationEn();
        default:
            throw new Error(`Locale not implemented for theme translation: ${locale}`);
    }
}
function createTranslationDe() {
    return {
        ...deDE,
        components: {
            ...deDE.components,
        },
    };
}
function createTranslationEn() {
    return {
        ...enUS,
        components: {
            ...enUS.components,
        },
    };
}
// pixel values taken from here https://www.figma.com/file/FnTGQdoYCd9Bu6T0czvxWD/Styleguide-Intern?node-id=680%3A12307
const spacings = [0, 2, 4, 8, 12, 16, 24, 32, 40, 48, 64, 96, 160];
export function createTheme(type, locale) {
    const palette = getPalette(type);
    const breakpoints = createBreakpoints({
        values: {
            xs: 0,
            sm: 518,
            md: 1023,
            lg: 1199,
            xl: 1536,
        },
    });
    const options = {
        supports: {
            flexGap: supportsFlexGap(),
        },
        palette,
        typography: {
            allVariants: {
                fontFamily: '"Zurich Sans", "Helvetica Neue", Arial, sans-serif', // Override for all typos
            },
            fontFamily: '"Zurich Sans", "Helvetica Neue", Arial, sans-serif', // Override for internal components  e.g Chip-root...
            fontWeightBold: font.weight.bold,
            fontWeightMedium: font.weight.medium,
            fontWeightRegular: font.weight.regular,
            fontWeightLight: font.weight.light,
            h1: {
                fontSize: font.size['4xl'],
                fontWeight: font.weight.light,
                letterSpacing: font.letterSpacing.xxl,
                color: palette.primary.dark,
                lineHeight: font.lineHeight.xs,
                [breakpoints.up('lg')]: {
                    fontSize: font.size['6xl'],
                },
            },
            h2: {
                fontSize: font.size.xxl,
                fontWeight: font.weight.light,
                letterSpacing: font.letterSpacing.xxl,
                color: palette.primary.dark,
                lineHeight: font.lineHeight.m,
                [breakpoints.up('lg')]: {
                    fontSize: font.size['5xl'],
                },
            },
            h3: {
                fontSize: font.size.xxl,
                fontWeight: font.weight.light,
                letterSpacing: font.letterSpacing.s,
                color: palette.primary.dark,
                lineHeight: font.lineHeight.m,
                [breakpoints.up('lg')]: {
                    fontSize: font.size['4xl'],
                },
            },
            h4: {
                fontSize: font.size['xl'],
                fontWeight: font.weight.light,
                letterSpacing: font.letterSpacing.s,
                color: palette.primary.dark,
                lineHeight: font.lineHeight.xs,
                [breakpoints.up('lg')]: {
                    fontSize: font.size['3xl'],
                },
            },
            h5: {
                fontSize: font.size.l,
                fontWeight: font.weight.light,
                letterSpacing: font.letterSpacing.s,
                color: palette.primary.dark,
                lineHeight: font.lineHeight.l,
                [breakpoints.up('lg')]: {
                    fontSize: font.size.xxl,
                },
            },
            h6: {
                fontSize: font.size.m,
                fontWeight: font.weight.regular,
                letterSpacing: font.letterSpacing.s,
                color: palette.primary.dark,
                lineHeight: font.lineHeight.s,
                [breakpoints.up('lg')]: {
                    fontSize: font.size.xl,
                },
            },
            body2: {
                fontSize: font.size.s,
                fontWeight: font.weight.light,
                letterSpacing: font.letterSpacing.s,
                color: palette.primary.dark,
                lineHeight: font.lineHeight.xs,
                [breakpoints.up('lg')]: {
                    fontSize: font.size.s,
                },
            },
            overline: {
                fontSize: font.size.xxs,
                fontWeight: font.weight.light,
                letterSpacing: font.letterSpacing.l,
                color: palette.primary.dark,
                lineHeight: font.lineHeight.m,
                textTransform: 'uppercase',
            },
            subtitle1: {
                fontSize: font.size.m,
                fontWeight: font.weight.light,
                letterSpacing: font.letterSpacing.l,
                color: palette.primary.dark,
                lineHeight: font.lineHeight.m,
                [breakpoints.up('lg')]: {
                    fontSize: font.size.l,
                },
            },
            subtitle2: {
                fontSize: font.size.m,
                fontWeight: font.weight.regular,
                letterSpacing: font.letterSpacing.s,
                color: color.grey[500],
                lineHeight: font.lineHeight.l,
            },
            body1: {
                fontSize: font.size.s,
                fontWeight: font.weight.light,
                letterSpacing: font.letterSpacing.m,
                color: color.grey[400],
                lineHeight: font.lineHeight.l,
                [breakpoints.up('lg')]: {
                    fontSize: font.size.m,
                },
            },
            button: {
                fontSize: font.size.m,
                fontWeight: font.weight.light,
                letterSpacing: font.letterSpacing.l,
                color: color.grey[900],
                lineHeight: font.lineHeight.xs,
                textTransform: 'none',
            },
            caption: {
                fontSize: font.size.xs,
                fontWeight: font.weight.light,
                letterSpacing: font.letterSpacing.l,
                color: color.grey[100],
                lineHeight: font.lineHeight.xl,
            },
        },
        shadows: [
            'none',
            '0px 1px 6px rgba(0,0,102, 0.16)',
            '0px 3px 6px rgba(0, 0, 102, 0.16)',
            '0px -3px 6px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
            '0px 8px 24px rgba(0, 0, 102, 0.16)',
        ],
        spacing: (factor) => {
            if (factor > 100) {
                return spacings[factor % 100];
            }
            return 8 * factor; // default mui we currently have.
        },
        shape: {
            borderRadius: 8,
        },
        components: {
            MuiStack: {
                defaultProps: {
                    useFlexGap: true,
                },
            },
            MuiTypography: {
                styleOverrides: {
                    gutterBottom: {
                        marginBottom: '16px',
                    },
                },
            },
            // TODO
            // MuiButton: {
            //   variants: [
            //     {
            //       props: { variant: 'link' },
            //       style: {
            //         textTransform: 'none',
            //         border: 'none',
            //       },
            //     },
            //   ],
            // },
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: color.grey['A100'],
                        minHeight: '100vh',
                        webkitOverflowScrolling: 'touch',
                    },
                    html: {
                        WebkitFontSmoothing: 'auto',
                    },
                    ul: {
                        paddingLeft: '20px',
                    },
                    li: {
                        fontWeight: 300,
                        fontSize: font.size.m,
                    },
                    '::-webkit-scrollbar': {
                        width: '12px',
                        height: '12px',
                    },
                    '::-webkit-scrollbar-thumb': {
                        background: '#bfbfbf',
                        borderRadius: '10px',
                        '&:hover': {
                            background: '#bfbfbf',
                        },
                    },
                    '::-webkit-scrollbar-track': {
                        background: 'white',
                        borderRadius: '10px',
                    },
                },
            },
            MuiStepConnector: {
                styleOverrides: {
                    lineVertical: {
                        borderColor: palette.secondary.main,
                        minHeight: 0,
                    },
                },
            },
            ...dsComponents,
        },
    };
    return createMuiTheme(options, createTranslation(locale));
}
// TODO: storybook implementation and workshop for devs and design how it works -> https://mui.com/customization/dark-mode/
const defaultPalette = {
    primary: {
        lighter: color.primary.skyBlueAA,
        light: color.primary.sandStone,
        main: color.primary.zurichBlue,
        dark: color.primary.darkBlue,
        contrastText: color.grey['A100'],
    },
    groupPrimary: color.primary,
    // TODO: maybe use grey directly instead of secondary
    secondary: {
        lighter: color.grey[900],
        light: color.grey[900],
        main: color.grey[500],
        dark: color.grey[100],
        contrastText: color.primary.zurichWhite,
    },
    azure: color.azure,
    teal: color.teal,
    mint: color.mint,
    grey: color.grey,
    lime: color.lime,
    white: {
        main: color.grey['A100'],
        dark: color.grey[900],
        contrastText: color.grey[100],
    },
    purple: {
        lighter: color.powderPink[100],
        light: color.candy['A100'],
        main: color.lilac[600],
        dark: color.lilac[100],
    },
    success: {
        light: color.moss[100],
        main: color.moss['A100'],
    },
    warning: {
        main: color.primary.orange100,
        light: color.primary.orange100,
    },
    error: {
        lighter: color.peach[200],
        light: color.peach[100],
        main: color.peach['A100'],
        dark: color.peach['A100'],
        contrastText: color.grey['A100'],
    },
    info: {
        main: color.primary.aliceBlue,
        light: color.primary.aliceBlue,
    },
    action: {
        disabled: color.grey[900],
    },
    text: {
        disabled: color.grey[900],
        secondary: color.grey[400],
    },
};
function getPalette(type) {
    // FIXME: Palette type does not work due to missing PaletteColor properties of defaultPalette
    return mergeDeepRight(defaultPalette, getExtendedPalette(type));
}
function getExtendedPalette(type) {
    switch (type) {
        // TODO: Are these colors up to date? Do we need them at all?
        case ThemeType.ZC: {
            // Zurich Connect special primary palette
            return {
                primary: {
                    lighter: color.primary.connectOrange,
                    light: color.primary.connectOrange,
                    main: color.primary.connectOrange,
                    dark: color.primary.darkBlue,
                    contrastText: color.primary.darkBlue, // TODO: Check if this is correct (Naci)
                },
            };
        }
    }
    return {};
}
function supportsFlexGap() {
    return (typeof window !== 'undefined' &&
        window.CSS.supports('(gap: 1em) and  (display: flex)'));
}
